import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceArea,
  ReferenceLine,
} from "recharts";

const CONTRAST_COLORS = [
  "#2563eb",
  "#e11d48",
  "#059669",
  "#d97706",
  "#7c3aed",
  "#0f766e",
  "#c026d3",
  "#ea580c",
  "#4f46e5",
  "#111827",
  "#65a30d",
  "#be123c",
];

export const SECTION_LABELS = {
  traeger_coefficients: "Динамика эмоциональной напряжённости",
  trigger_coefficients: "Сезонный индекс эмоциональной напряжённости",
  activity_log: "Активность дискуссий",
  text_markers_chart: "Психолингвистические показатели",
  text_anomality_chart: "Аномальность текстов",
  topics_intensity_chart: "Интенсивность тематик",
  graph_quarters: "Динамика по кварталам",
  time_series_analysis: "Временные ряды индекса",
  top_influencers: "Топ участников по влиянию",
  srl_wordcloud: "Облако каузаторов",
  vk_group_stats: "Статистика сообществ",
};

const COLUMN_LABELS = {
  date: "Дата",
  time: "Дата и время",
  quarter: "Квартал",
  interval_start: "Начало интервала",
  interval_end: "Конец интервала",
  group_id: "Группа",
  community_id: "Сообщество",
  public_id: "Сообщество",
  user_id: "Пользователь",
  marker: "Показатель",
  dict: "Тема",
  intensity: "Интенсивность",
  activity: "Активность",
  avg_activity: "Средняя активность",
  alarm_level: "Уровень тревоги",
  white_count: "Белые дни",
  black_count: "Чёрные дни",
  white_percentage: "Белые дни, %",
  black_percentage: "Чёрные дни, %",
  total_records: "Всего записей",
  traeger_coefficient: "Коэффициент Трегера",
  index: "Индекс",
  trendUp: "Рост тренда",
  trendDown: "Снижение тренда",
};

const MARKER_LABELS = {
  day: "День",
  week: "Неделя",
  month: "Месяц",
  trager_coef: "Коэф. Трегера",
  sentiment_rate: "Оценка настроения",
  negation_rate: "Доля отрицаний",
  text_anomality: "Аномальность текста",
  logical_coh_coef: "Логическая связность",
  tgw_positive_assessment: "Позитивная оценка",
  tgw_positive_social: "Позитивное социальное",
  tgw_positive_emotions: "Позитивные эмоции",
  tgw_negative_assessment: "Негативная оценка",
  tgw_negative_social: "Негативное социальное",
  tgw_negative_emotions: "Негативные эмоции",
  tgw_motivation_activity: "Мотивация",
  tgw_cognitive_communication: "Когнитивная связь",
  tgw_destructive_activity: "Деструктивность",
  tgw_affect_lex: "Аффективная лексика",
  tgw_bodily_states_emotions: "Телесные эмоции",
  tgw_invectives: "Инвективы",
  tgw_soft_invectives: "Мягкие инвективы",
  tgw_obscene_lex: "Нецензурная лексика",
  tgw_youth_jargon: "Молодёжный жаргон",
  tgw_hcs: "Человеческие связи",
  tgw_economics: "Экономика",
  tgw_catastrophes: "Катастрофы",
  tgw_security_structures: "Силовые структуры",
  tgw_healthcare_demography_ecology: "Здравоохранение",
  tgw_authority: "Авторитет",
  be_disgust: "Отвращение",
  be_shame: "Стыд",
  be_anger: "Гнев",
  be_fear: "Страх",
  be_sadness: "Грусть",
  be_calm_excitement: "Спокойствие/волнение",
  be_happyness: "Счастье",
  be_wonder: "Удивление",
  ew_positive: "Позитивные эмоции (EW)",
  ew_negative: "Негативные эмоции (EW)",
  ew_ambivalent: "Амбивалентные эмоции",
  ew_de_emotives: "Деэмотивы",
};

function parseAnalysisSpec(value) {
  const [base, ...rest] = String(value || "").split(":");
  return { base, parameter: rest.join(":") };
}

export function humanizeReportKey(key) {
  const text = String(key || "");
  if (text.includes(":")) {
    const { base, parameter } = parseAnalysisSpec(text);
    return `${humanizeReportKey(base)}: ${humanizeReportKey(parameter)}`;
  }
  if (text.startsWith("tgw/")) {
    return humanizeReportKey(text.slice(4));
  }
  return COLUMN_LABELS[text] || MARKER_LABELS[text] || SECTION_LABELS[text] || text.replace(/_/g, " ");
}

function isNumeric(v) {
  return typeof v === "number" && !Number.isNaN(v);
}

function colorForIndex(index, total) {
  if (total <= 1) return "#2563eb";
  return CONTRAST_COLORS[index % CONTRAST_COLORS.length];
}

function formatDateTick(value) {
  const text = String(value || "");
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const [y, m, d] = text.slice(0, 10).split("-");
    return `${d}.${m}.${y}`;
  }
  return text;
}

function formatQuarter(value) {
  const text = String(value || "");
  const match = /^(\d{4})-(\d{2})/.exec(text);
  if (!match) return text;
  const month = Number(match[2]);
  return `${match[1]}-Q${Math.floor((month - 1) / 3) + 1}`;
}

function getTraegerRanges(rows) {
  const values = rows.map((row) => Number(row.traeger_coefficient)).filter(Number.isFinite);
  if (!values.length) {
    return { domain: [0, 2], green: [0.4, 1.25], yellow: [[0, 0.4], [1.25, 1.7]], red: [[1.7, 2]] };
  }
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  const std = Math.sqrt(variance) || 0.4;
  const minValue = Math.min(...values, mean - 3 * std);
  const maxValue = Math.max(...values, mean + 3 * std);
  return {
    domain: [Math.floor(Math.min(0, minValue) * 10) / 10, Math.ceil(maxValue * 10) / 10],
    green: [mean - std, mean + std],
    yellow: [[mean - 2 * std, mean - std], [mean + std, mean + 2 * std]],
    red: [[Math.min(minValue, mean - 3 * std), mean - 2 * std], [mean + 2 * std, Math.max(maxValue, mean + 3 * std)]],
  };
}

function pickXKey(row) {
  if (!row || typeof row !== "object") return null;
  const candidates = ["date", "quarter", "interval_start", "time"];
  for (const k of candidates) {
    if (row[k] != null && row[k] !== "") return k;
  }
  return null;
}

function numericKeysForRows(rows, xKey) {
  if (!rows?.length) return [];
  const keys = new Set();
  for (const row of rows.slice(0, 30)) {
    Object.keys(row).forEach((k) => {
      if (k === xKey) return;
      if (k === "dict" || k === "public_id" || k === "user_id" || k === "community_id" || k === "marker") return;
      const v = row[k];
      if (isNumeric(v)) keys.add(k);
    });
  }
  return Array.from(keys);
}

function pivotActivityByGroup(rows, groupLabelMap = {}) {
  const byDate = new Map();
  const lineKeys = [];
  const labels = {};
  rows.forEach((row) => {
    const date = String(row.date || row.time || "");
    const groupId = String(row.public_id || row.group_id || "");
    if (!date || !groupId) return;
    const key = `group_${groupId}`;
    if (!lineKeys.includes(key)) {
      lineKeys.push(key);
      labels[key] = groupLabelMap[groupId] || `Сообщество ${groupId}`;
    }
    if (!byDate.has(date)) byDate.set(date, { date });
    byDate.get(date)[key] = Number(row.activity) || 0;
  });
  return {
    rows: Array.from(byDate.values()).sort((a, b) => String(a.date).localeCompare(String(b.date))),
    xKey: "date",
    lineKeys,
    labels,
  };
}

function pivotTopicsIntensity(rows) {
  const byDate = new Map();
  for (const r of rows) {
    const d = r.date;
    if (d == null) continue;
    const dictKey = String(r.dict ?? "unknown");
    if (!byDate.has(d)) {
      byDate.set(d, { date: typeof d === "string" ? d : d?.toString?.() || String(d) });
    }
    const o = byDate.get(d);
    const y = Number(r.intensity);
    o[dictKey] = Number.isFinite(y) ? y : null;
  }
  return Array.from(byDate.values()).sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

function ReportSectionChart({ analysisType, section, groupLabelMap = {} }) {
  const baseAnalysisType = section?.base_analysis_type || parseAnalysisSpec(analysisType).base;
  const err = section?.error;
  const data = section?.data;

  const wordcloudEntries = useMemo(() => {
    if (!data || Array.isArray(data) || typeof data !== "object") return null;
    if (baseAnalysisType !== "srl_wordcloud") return null;
    return Object.entries(data)
      .map(([word, count]) => ({ word, count: Number(count) || 0 }))
      .filter((e) => e.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 50);
  }, [data, baseAnalysisType]);

  const chartModel = useMemo(() => {
    if (err || data == null) return { kind: "none" };
    if (wordcloudEntries) {
      return { kind: "barWords", rows: wordcloudEntries };
    }
    if (Array.isArray(data)) {
      if (data.length === 0) return { kind: "empty" };
      if (baseAnalysisType === "traeger_coefficients") {
        return {
          kind: "traeger",
          rows: data.map((r) => ({ ...r, date: String(r.date || "") })),
          ranges: getTraegerRanges(data),
        };
      }
      if (baseAnalysisType === "topics_intensity_chart") {
        const pivoted = pivotTopicsIntensity(data);
        const xKey = "date";
        const lineKeys = numericKeysForRows(pivoted, xKey);
        return { kind: "line", rows: pivoted, xKey, lineKeys, labels: Object.fromEntries(lineKeys.map((k) => [k, humanizeReportKey(k)])) };
      }
      if (baseAnalysisType === "activity_log") {
        return { kind: "line", ...pivotActivityByGroup(data, groupLabelMap) };
      }
      if (baseAnalysisType === "time_series_analysis") {
        return { kind: "blackWhite", rows: data, mode: "percent" };
      }
      if (baseAnalysisType === "graph_quarters") {
        return {
          kind: "quarters",
          rows: data.map((r) => ({ ...r, quarter_label: formatQuarter(r.quarter) })),
        };
      }
      if (data.some((row) => row && row.black_days != null && row.white_days != null)) {
        return { kind: "blackWhite", rows: data, mode: "count" };
      }
      const xKey = pickXKey(data[0]);
      if (!xKey) {
        return { kind: "table", rows: data };
      }
      const lineKeys = numericKeysForRows(data, xKey);
      if (!lineKeys.length) {
        return { kind: "table", rows: data };
      }
      const rows = data.map((r) => ({
        ...r,
        [xKey]: r[xKey] != null ? String(r[xKey]) : "",
      }));
      return { kind: "line", rows, xKey, lineKeys, labels: Object.fromEntries(lineKeys.map((k) => [k, humanizeReportKey(k)])) };
    }
    if (typeof data === "object") {
      return { kind: "kv", obj: data };
    }
    return { kind: "text", text: String(data) };
  }, [data, err, baseAnalysisType, wordcloudEntries, groupLabelMap]);

  if (err) {
    return (
      <div className="table-reports__error" style={{ marginTop: 4 }}>
        {String(err)}
      </div>
    );
  }

  if (chartModel.kind === "empty") {
    return <div className="table-reports__empty">Нет данных для этой секции.</div>;
  }

  if (chartModel.kind === "barWords") {
    const rows = chartModel.rows;
    return (
      <div style={{ width: "100%", height: 420 }}>
        <ResponsiveContainer>
          <BarChart data={rows} layout="vertical" margin={{ left: 80, right: 16, top: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis type="category" dataKey="word" width={72} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="count" fill="#2563eb" name="Частота" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chartModel.kind === "traeger") {
    const { rows, ranges } = chartModel;
    return (
      <div style={{ width: "100%", height: 420 }}>
        <ResponsiveContainer>
          <LineChart data={rows} margin={{ left: 12, right: 16, top: 8, bottom: 54 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateTick}
              tick={{ fontSize: 11, angle: -45, textAnchor: "end" }}
              interval="preserveStartEnd"
              minTickGap={28}
            />
            <YAxis domain={ranges.domain} tick={{ fontSize: 11 }} />
            <Tooltip labelFormatter={formatDateTick} />
            <ReferenceArea y1={ranges.red[0][0]} y2={ranges.red[0][1]} fill="#ef4444" fillOpacity={0.14} />
            <ReferenceArea y1={ranges.yellow[0][0]} y2={ranges.yellow[0][1]} fill="#f59e0b" fillOpacity={0.16} />
            <ReferenceArea y1={ranges.green[0]} y2={ranges.green[1]} fill="#22c55e" fillOpacity={0.18} />
            <ReferenceArea y1={ranges.yellow[1][0]} y2={ranges.yellow[1][1]} fill="#f59e0b" fillOpacity={0.16} />
            <ReferenceArea y1={ranges.red[1][0]} y2={ranges.red[1][1]} fill="#ef4444" fillOpacity={0.14} />
            <ReferenceLine y={ranges.green[0]} stroke="#111827" strokeWidth={1} />
            <ReferenceLine y={ranges.green[1]} stroke="#111827" strokeWidth={1} />
            <Legend wrapperStyle={{ color: "#111827", paddingTop: 20 }} />
            <Line
              type="monotone"
              dataKey="traeger_coefficient"
              name="Эмоциональная напряжённость"
              stroke="#2563eb"
              dot={{ r: 3, fill: "#fff", stroke: "#2563eb", strokeWidth: 2 }}
              strokeWidth={2}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chartModel.kind === "line") {
    const { rows, xKey, lineKeys, labels = {} } = chartModel;
    return (
      <div style={{ width: "100%", height: 380 }}>
        <ResponsiveContainer>
          <LineChart data={rows} margin={{ left: 8, right: 16, top: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey={xKey}
              name={humanizeReportKey(xKey)}
              tickFormatter={formatDateTick}
              tick={{ fontSize: 11, angle: -45, textAnchor: "end" }}
              interval="preserveStartEnd"
              minTickGap={24}
              height={60}
            />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip labelFormatter={formatDateTick} />
            <Legend wrapperStyle={{ color: "#111827" }} />
            {lineKeys.map((k, i) => (
              <Line
                key={k}
                type="monotone"
                dataKey={k}
                name={labels[k] || humanizeReportKey(k)}
                stroke={colorForIndex(i, lineKeys.length)}
                dot={false}
                strokeWidth={2}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chartModel.kind === "quarters") {
    const rows = chartModel.rows || [];
    return (
      <div style={{ width: "100%", height: 420 }}>
        <ResponsiveContainer>
          <ComposedChart data={rows} margin={{ left: 8, right: 16, top: 8, bottom: 48 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="quarter_label" tick={{ fontSize: 11, angle: -45, textAnchor: "end" }} height={54} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend wrapperStyle={{ color: "#111827" }} />
            <Bar dataKey="bar_value" name="Индекс сезонной эмоциональной напряжённости" fill="#3b82f6" stroke="#1d4ed8" />
            <Line type="monotone" dataKey="red_value" name="Уровень агрессии" stroke="#ef4444" dot={{ r: 4 }} strokeWidth={2} />
            <Line type="monotone" dataKey="green_value" name="Уровень апатии" stroke="#22c55e" dot={{ r: 4 }} strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chartModel.kind === "blackWhite") {
    const rows = chartModel.rows || [];
    const whiteKey = chartModel.mode === "count" ? "white_days" : "white_percentage";
    const blackKey = chartModel.mode === "count" ? "black_days" : "black_percentage";
    const whiteName = chartModel.mode === "count" ? "Белые дни" : "Белые дни, %";
    const blackName = chartModel.mode === "count" ? "Чёрные дни" : "Чёрные дни, %";
    const xKey = chartModel.mode === "count" ? "quarter" : "interval_start";
    return (
      <div style={{ width: "100%", height: 380 }}>
        <ResponsiveContainer>
          <BarChart data={rows} margin={{ left: 8, right: 16, top: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend wrapperStyle={{ color: "#111827" }} />
            <Bar dataKey={whiteKey} name={whiteName} fill="#ffffff" stroke="#000000" />
            <Bar dataKey={blackKey} name={blackName} fill="#334155" stroke="#1e293b" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chartModel.kind === "table") {
    const rows = chartModel.rows;
    const cols = rows.length ? Object.keys(rows[0]) : [];
    const columnChunks = cols.length > 8
      ? Array.from({ length: Math.ceil((cols.length - 1) / 6) }, (_, i) => [cols[0], ...cols.slice(1 + i * 6, 1 + (i + 1) * 6)])
      : [cols];
    return (
      <>
        {columnChunks.map((chunk, chunkIndex) => {
          return (
            <div key={chunkIndex} className="table-reports__table-wrap" style={{ maxHeight: 360, marginBottom: 12 }}>
              <table>
                <thead>
                  <tr>
                    {chunk.map((c) => (
                      <th key={c}>{humanizeReportKey(c)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 200).map((r, i) => (
                    <tr key={i}>
                      {chunk.map((c) => (
                        <td key={c}>
                          {c === "vk_link" && r[c] ? (
                            <a href={String(r[c])} target="_blank" rel="noreferrer">Открыть профиль</a>
                          ) : (
                            r[c] != null ? String(r[c]) : ""
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </>
    );
  }

  if (chartModel.kind === "kv") {
    return (
      <pre style={{ fontSize: 12, overflow: "auto", maxHeight: 240, margin: 0 }}>
        {JSON.stringify(chartModel.obj, null, 2)}
      </pre>
    );
  }

  if (chartModel.kind === "text") {
    return <div className="table-reports__meta">{chartModel.text}</div>;
  }

  return <div className="table-reports__empty">Нет данных.</div>;
}

export default ReportSectionChart;
